import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { invoicesApi, type ReceiptVerificationResult } from "@/lib/api/invoices";
import { useTenant } from "@/contexts/TenantContext";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, ArrowLeft, BookOpen, ShieldCheck } from "lucide-react";
import { cn } from "@/lib/utils";

function currencySymbol(code: string): string {
  const c = (code || "NGN").toUpperCase();
  const map: Record<string, string> = {
    NGN: "₦",
    USD: "$",
    EUR: "€",
    GBP: "£",
  };
  return map[c] || `${c} `;
}

function ReceiptRow({
  label,
  children,
  className,
}: {
  label: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between sm:gap-6 py-3 border-b border-slate-100 last:border-b-0",
        className
      )}
    >
      <dt className="text-sm font-medium text-slate-600 shrink-0">{label}</dt>
      <dd className="text-sm text-slate-900 text-left sm:text-right sm:min-w-0 sm:max-w-[65%]">
        {children}
      </dd>
    </div>
  );
}

export default function VerifyReceiptPage() {
  const { tenant } = useTenant();
  const [params] = useSearchParams();
  const invoiceId = params.get("i") || "";
  const token = params.get("t") || "";
  const [result, setResult] = useState<ReceiptVerificationResult | null>(null);
  const [loading, setLoading] = useState(true);

  const footerTenantName =
    result?.valid === true
      ? result.libraryFullName?.trim() || result.tenantName
      : tenant?.name?.trim() || null;

  useEffect(() => {
    if (!invoiceId || !token) {
      setResult({ valid: false, message: "This link is missing required parameters." });
      setLoading(false);
      return;
    }
    invoicesApi
      .verifyReceipt(invoiceId, token)
      .then(setResult)
      .catch(() =>
        setResult({
          valid: false,
          message:
            "We could not verify this receipt. The link may have expired or the service may be temporarily unavailable.",
        })
      )
      .finally(() => setLoading(false));
  }, [invoiceId, token]);

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-10 sm:py-12">
        <div className="mx-auto max-w-xl">
          <Button
            variant="ghost"
            size="sm"
            className="mb-6 -ml-2 h-10 gap-2 text-slate-600 hover:text-slate-900 hover:bg-slate-100/80"
            asChild
          >
            <Link to="/">
              <ArrowLeft className="h-4 w-4" aria-hidden />
              Back to home
            </Link>
          </Button>

          <article
            className="overflow-hidden rounded-xl border border-slate-200 bg-white"
            aria-labelledby="verify-receipt-title"
          >
            {/* Top accent — brand primary */}
            {/* <div className="h-1 bg-primary" aria-hidden /> */}

            <header className="border-b border-slate-100 px-6 pt-6 pb-5 sm:px-8">
              <div className="flex items-start gap-3">
                <div
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary"
                  aria-hidden
                >
                  <BookOpen className="h-5 w-5" strokeWidth={2} />
                </div>
                <div className="min-w-0 space-y-1">
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                    Library payment
                  </p>
                  <h1
                    id="verify-receipt-title"
                    className="font-heading text-xl font-bold tracking-tight text-slate-900 sm:text-2xl"
                  >
                    Receipt verification
                  </h1>
                  <p className="text-sm leading-relaxed text-slate-600">
                    Use this page to confirm that a fee receipt was issued by the library and
                    matches our records.
                  </p>
                </div>
              </div>
            </header>

            <div className="px-6 py-6 sm:px-8 sm:py-8">
              {loading ? (
                <div
                  className="flex flex-col items-center justify-center gap-4 py-10"
                  role="status"
                  aria-live="polite"
                >
                  <div className="relative">
                    <div className="h-12 w-12 rounded-full border-2 border-slate-200 border-t-primary animate-spin" />
                  </div>
                  <p className="text-sm font-medium text-slate-600">Verifying receipt…</p>
                  <span className="sr-only">Loading verification result</span>
                </div>
              ) : result?.valid ? (
                <div className="space-y-6">
                  <div
                    className="flex gap-3 rounded-lg border border-green-200 bg-green-50 p-4"
                    role="status"
                  >
                    <ShieldCheck
                      className="h-6 w-6 shrink-0 text-green-600"
                      strokeWidth={2}
                      aria-hidden
                    />
                    <div className="min-w-0">
                      <p className="font-heading text-base font-semibold text-green-900">
                        Verified receipt
                      </p>
                      <p className="mt-0.5 text-sm text-green-800">
                        This receipt is authentic and corresponds to a completed payment in our
                        system.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 text-green-700">
                    <CheckCircle2 className="h-5 w-5 shrink-0" strokeWidth={2} aria-hidden />
                    <span className="text-sm font-medium">
                      Details below match the issued receipt.
                    </span>
                  </div>

                  <dl className="rounded-lg border border-slate-200 bg-slate-50/80 px-4 sm:px-5">
                    <ReceiptRow label="Library">
                      <span className="font-medium">
                        {result.libraryFullName?.trim() || result.tenantName}
                      </span>
                    </ReceiptRow>
                    {result.libraryAddress?.trim() ? (
                      <ReceiptRow label="Address">
                        <span className="whitespace-pre-line">{result.libraryAddress.trim()}</span>
                      </ReceiptRow>
                    ) : null}
                    <ReceiptRow label="Receipt no.">
                      <span className="font-mono text-sm tabular-nums">{result.invoiceNumber}</span>
                    </ReceiptRow>
                    <ReceiptRow label="Paid by">{result.patronName}</ReceiptRow>
                    <ReceiptRow label="Description">{result.description}</ReceiptRow>
                    {result.receiptLabel ? (
                      <ReceiptRow label="Resource receipt ID">
                        <span className="font-mono text-sm">{result.receiptLabel}</span>
                      </ReceiptRow>
                    ) : null}
                    {result.resourceTitle ? (
                      <ReceiptRow label="Resource">{result.resourceTitle}</ReceiptRow>
                    ) : null}
                    {result.patronTypeNameAtPurchase ? (
                      <ReceiptRow label="Patron category">
                        {result.patronTypeNameAtPurchase}
                      </ReceiptRow>
                    ) : null}
                    {result.type === "digital_resource" ? (
                      <ReceiptRow label="Access expires">
                        {result.accessExpiresAt
                          ? new Date(result.accessExpiresAt).toLocaleDateString(undefined, {
                              dateStyle: "medium",
                            })
                          : result.accessDurationDays == null
                            ? "Permanent"
                            : "—"}
                      </ReceiptRow>
                    ) : null}
                    <ReceiptRow label="Amount">
                      <span className="font-semibold tabular-nums">
                        {currencySymbol(result.currency)}
                        {result.amountPaid.toLocaleString(undefined, {
                          minimumFractionDigits: 0,
                          maximumFractionDigits: 2,
                        })}{" "}
                        <span className="text-xs font-normal text-slate-500">
                          {result.currency}
                        </span>
                      </span>
                    </ReceiptRow>
                    <ReceiptRow label="Date paid">
                      {new Date(result.paidAt).toLocaleString(undefined, {
                        dateStyle: "medium",
                        timeStyle: "short",
                      })}
                    </ReceiptRow>
                  </dl>
                </div>
              ) : (
                <div className="space-y-4" role="alert" aria-labelledby="verify-error-title">
                  <div className="flex flex-col items-center gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-8 text-center sm:py-10">
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
                      <XCircle className="h-7 w-7 text-red-600" strokeWidth={2} aria-hidden />
                    </div>
                    <div className="max-w-sm space-y-2">
                      <p
                        id="verify-error-title"
                        className="font-heading text-lg font-semibold text-red-900"
                      >
                        Receipt could not be verified
                      </p>
                      <p className="text-sm leading-relaxed text-red-800">
                        {result?.message ||
                          "We could not match this link to a valid payment receipt."}
                      </p>
                    </div>
                  </div>
                  <p className="text-center text-xs text-slate-500">
                    If you paid recently, check your email for an updated receipt or contact the
                    library with your payment reference.
                  </p>
                </div>
              )}
            </div>
          </article>

          {footerTenantName ? (
            <p className="mt-6 text-center text-xs text-slate-500">{footerTenantName}</p>
          ) : null}
        </div>
      </div>
    </div>
  );
}
